import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import { motion } from 'framer-motion';
import AdBanner from '../AdPage/AdBanner';
// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const BusinessLoanEmiCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(8);
  const [loanTenure, setLoanTenure] = useState(5);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let currentYear = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === '' ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'EMI Breakdown',
        data: [loanAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            question: "What is the maximum tenure allowed for business loan EMIs?",
            answer: "Tenure typically ranges from 12 months to 60 months. Some lenders offer longer terms depending on business type and stability."
          },
          {
            question: "Can I use this calculator for working capital loans?",
            answer: "Yes, the calculator is suitable for any term-based loan, including working capital loans."
          },
          {
            question: "Does the calculator include processing fees?",
            answer: "No, the calculator provides EMI based on principal and interest only. Processing fees are usually paid upfront and vary by lender."
          },
          {
            question: "What are the advantages of knowing EMI beforehand?",
            answer: "It helps manage finances better, compare lenders, and avoid overborrowing that could strain your cash flow."
          },
          {
            question: "How does tenure affect my EMI?",
            answer: "Longer tenure reduces EMI but increases total interest. Shorter tenure results in higher EMIs but lower overall interest payout."
          },
          {
            question: "What if I miss an EMI payment?",
            answer: "Missing EMIs may lead to penalties and impact your business credit score, affecting future borrowings."
          },
          {
            question: "Is the EMI fixed for the entire loan duration?",
            answer: "Yes, if you opt for a fixed interest rate. With floating rates, the EMI may change during the tenure."
          },
          {
            question: "Can I calculate EMI on a line of credit?",
            answer: "No. EMI calculations are accurate for term loans only. Line of credit EMIs are usage-based and variable."
          },
          {
            question: "Is the EMI calculator free to use?",
            answer: "Yes, TheFinMaster’s calculator is completely free and accessible 24/7."
          },
          {
            question: "How can I reduce my EMI burden?",
            answer: "Negotiate for better rates, increase tenure, or make part-prepayments periodically."
          },
          {
            question: "What documents are required to apply for a business loan?",
            answer: "Typically PAN, Aadhaar, GST filings, bank statements, ITRs, and business registration proof."
          },
          {
            question: "Can startups apply for business loans?",
            answer: "Yes, but eligibility is assessed on business model, projections, and promoter credentials."
          },
          {
            question: "Is collateral required for business loans?",
            answer: "Many lenders offer collateral-free loans up to a certain limit. Higher amounts may need security."
          },
          {
            question: "How do I know if my EMI is affordable?",
            answer: "Ensure that your monthly EMI doesn’t exceed 30–40% of your net monthly business income."
          },
          {
            question: "Will this EMI calculation match what the bank offers?",
            answer: "The calculation is highly accurate, but slight variations may occur based on bank-specific fees or rate fluctuations."
          }
        ]
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
    const calculators = [
      {
        name: 'SBI Business Loan Calculator',
        path: '/financial-tools-calculators/sbi-business-loan-emi-calculator',
        image: '/assets/sb.png',
      },
      {
        name: 'HDFC Business Loan Calculator',
        path: '/financial-tools-calculators/hdfc-business-loan-emi-calculator',
        image: '/assets/hd.png',
      },
      {
        name: 'ICICI Business Loan Calculator',
        path: '/financial-tools-calculators/icici-business-loan-emi-calculator',
        image: '/assets/icc.png',
      },
      {
        name: 'Axis Business  Loan Calculator',
        path: '/financial-tools-calculators/axis-business-loan-emi-calculator',
        image: '/assets/ax.png',
      },
      {
        name: 'BOB Business  Loan Calculator',
        path: '/financial-tools-calculators/bank-of-baroda-business-loan-emi-calculator',
        image: '/assets/bb.png',
      },
      {
        name: 'Kotak Business  Loan Calculator',
        path: '/financial-tools-calculators/kotak-business-loan-emi-calculator',
        image: '/assets/kot.png',
      },
      {
        name: 'PNB Business  Loan Calculator',
        path: '/financial-tools-calculators/pnb-business-loan-emi-calculator',
        image: '/assets/pn.png',
      },
      {
        name: 'IDFC Business  Loan Calculator',
        path: '/financial-tools-calculators/idfc-business-loan-emi-calculator',
        image: '/assets/idd1.png',
      },
      {
        name: 'Union Bank Business Loan Calculator',
        path: '/financial-tools-calculators/union-bank-of-india-business-loan-emi-calculator',
        image: '/assets/un.png',
      },
      {
        name: 'Canara Business  Loan Calculator',
        path: '/financial-tools-calculators/canara-business-loan-emi-calculator',
        image: '/assets/can.png',
      },
     
     
      // {
      //   name: 'Central Bank of India Gold  Loan Calculator',
      //   path: '/financial-tools-calculators/central-bank-of-india-gold-loan-emi-calculator',
      //   image: '/assets/ax.png',
      // },
      // {
      //   name: 'Indian Bank Gold  Loan Calculator',
      //   path: '/financial-tools-calculators/indian-bank-gold-loan-emi-calculator',
      //   image: '/assets/ax.png',
      // },
   
     
   
     
     
    ];
  return (
    <>
     <Helmet>
         <title>Business Loan EMI Calculator – Estimate Repayment Schedule</title>
        <meta  name='description' content='Estimate monthly EMIs and total repayment amount for your business loan using our EMI calculator.'/>
         <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/business-loan-emi-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" /> 
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Business Loan EMI Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
      <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
        <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">Business Loan EMI Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanAmount}
                onChange={handleLoanAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={loanTenure}
                onChange={handleLoanTenureChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{loanTenure} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={loanTenure}
              onChange={(e) => setLoanTenure(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateEMI}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
            <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
        {amortizationSchedule.length > 0 && (
          <div className="flex flex-col space-y-4">
            {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
              <div key={idx} className="mb-4 border rounded-md shadow-md">
                <div
                  className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                  onClick={() => handleYearToggle(year)}
                >
                  <span>{year}</span>
                  <span>{activeYear === year ? '-' : '+'}</span>
                </div>
                {activeYear === year && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-md shadow-md">
                      <thead>
                        <tr>
                          <th className="py-2 px-4 border-b">Month</th>
                          <th className="py-2 px-4 border-b">Principal Paid</th>
                          <th className="py-2 px-4 border-b">Interest Charged</th>
                          <th className="py-2 px-4 border-b">Total Payment</th>
                          <th className="py-2 px-4 border-b">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                            <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    
         <div className="p-4 sm:p-8 bg-white">
                  <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                  Business Loan EMI Calculator 
                  </h2>
            
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                          {calculators.map((calc, index) => (
                            <motion.div
                              key={index}
                              whileHover={{ scale: 1.04 }}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                              <Link
                                to={calc.path}
                                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                              >
                                <img
                                  src={calc.image}
                                  alt={calc.name}
                                  className="w-16 h-16 object-contain mb-2"
                                />
                                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                  {calc.name}
                                </span>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Instantly Calculate Your Business Loan EMI
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            A business loan can be a game-changer for entrepreneurs, SMEs, and established businesses seeking to grow their operations, manage working capital, or invest in expansion. However, understanding your repayment obligations is crucial. TheFinMaster’s Business Loan EMI Calculator is a powerful online tool that helps you plan your loan repayments with clarity and precision. By offering real-time calculations, it ensures that your financial planning is always on point, allowing you to compare various loan options and make informed decisions without any guesswork.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Whether you're planning to buy machinery, expand your office space, or handle seasonal cash flow issues, this calculator gives you a complete EMI breakdown, helping you budget better and avoid financial strain in the long run.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Business Loan EMI?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Business Loan EMI (Equated Monthly Instalment) is the fixed amount you pay every month towards the repayment of your business loan. This EMI comprises two components: the principal amount and the interest charged on the loan. The EMI is structured in such a way that, in the early stages of the tenure, a larger portion goes towards interest, and in later stages, the principal component increases.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This predictable monthly payment schedule helps business owners plan their finances efficiently. Unlike variable repayment structures, EMIs provide financial discipline, making it easier to manage cash flow. Knowing your EMI in advance helps ensure you can meet other operational expenses while repaying the loan.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s Business Loan EMI Calculator?
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant Calculations:</strong> Save time and effort with quick EMI calculations in seconds.</li>
              <li><strong>No Registration Required:</strong> Use freely without signing up or entering personal information.</li>
              <li><strong>Improved Financial Planning:</strong> Know your future liabilities to plan cash flow effectively.</li>
              <li><strong>Compare Multiple Loan Scenarios:</strong> Experiment with different interest rates, loan amounts, and tenures to find the optimal EMI.</li>
              <li><strong>Avoid Human Error:</strong> Manual EMI calculations can be complex and error-prone; our calculator ensures 100% accuracy.</li>
              <li><strong>Understand Cost of Borrowing:</strong> Get a clear idea of total interest payable and the overall repayment amount.</li>
              <li><strong>Supports Informed Decisions:</strong> Avoid surprises and ensure your chosen EMI aligns with your monthly revenue cycle.</li>
              <li><strong>User-Friendly Interface:</strong> A clean, intuitive design that caters to seasoned professionals and new entrepreneurs alike.</li>
              <li><strong>No Impact on Credit Score:</strong> Using the calculator is risk-free and doesn’t affect your credit rating.</li>
              <li><strong>Device Compatibility:</strong> Accessible across desktops, tablets, and mobile devices without the need for an app.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Business Loan EMI Calculator Work?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-xl">
              The calculator is built on a simple financial formula that determines EMI based on principal, interest rate, and loan tenure.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>EMI Formula:</strong><br />
              EMI = [P x R x (1+R)^N] / [(1+R)^N - 1]
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Loan Amount (Principal)</li>
              <li>R = Monthly interest rate (annual rate / 12 / 100)</li>
              <li>N = Loan tenure in months</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Suppose you take a business loan of ₹5,00,000 at an annual interest rate of 12% for 3 years (36 months):<br />
              Monthly Interest Rate = 12/12/100 = 0.01<br />
              EMI = [500000 x 0.01 x (1+0.01)^36] / [(1+0.01)^36 - 1]<br />
              EMI = ₹16,607 (approx.)<br />
              This monthly outflow includes interest and principal components which vary every month.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Amortization Schedule
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An amortization schedule provides a detailed table showing the breakdown of each EMI into principal and interest components. Over time, the interest portion decreases, and the principal repayment increases.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Sample Amortization Table for ₹5,00,000 Loan at 12% for 12 Months:</strong>
            </p>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Month</th>
                    <th className="border px-4 py-2 text-left">EMI (₹)</th>
                    <th className="border px-4 py-2 text-left">Principal (₹)</th>
                    <th className="border px-4 py-2 text-left">Interest (₹)</th>
                    <th className="border px-4 py-2 text-left">Outstanding Principal (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">1</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">39,389</td>
                    <td className="border px-4 py-2">5,000</td>
                    <td className="border px-4 py-2">4,60,611</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">2</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">39,782</td>
                    <td className="border px-4 py-2">4,607</td>
                    <td className="border px-4 py-2">4,20,829</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">3</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">40,180</td>
                    <td className="border px-4 py-2">4,209</td>
                    <td className="border px-4 py-2">3,80,649</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">4</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">40,582</td>
                    <td className="border px-4 py-2">3,807</td>
                    <td className="border px-4 py-2">3,40,067</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">5</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">40,988</td>
                    <td className="border px-4 py-2">3,401</td>
                    <td className="border px-4 py-2">2,99,079</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">6</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">41,398</td>
                    <td className="border px-4 py-2">2,991</td>
                    <td className="border px-4 py-2">2,57,681</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">7</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">41,812</td>
                    <td className="border px-4 py-2">2,577</td>
                    <td className="border px-4 py-2">2,15,869</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">8</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">42,230</td>
                    <td className="border px-4 py-2">2,159</td>
                    <td className="border px-4 py-2">1,73,639</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">9</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">42,652</td>
                    <td className="border px-4 py-2">1,737</td>
                    <td className="border px-4 py-2">1,30,987</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">10</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">43,079</td>
                    <td className="border px-4 py-2">1,310</td>
                    <td className="border px-4 py-2">87,908</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">11</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">43,510</td>
                    <td className="border px-4 py-2">879</td>
                    <td className="border px-4 py-2">44,398</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">12</td>
                    <td className="border px-4 py-2">44,389</td>
                    <td className="border px-4 py-2">44,398</td>
                    <td className="border px-4 py-2">-</td>
                    <td className="border px-4 py-2">0</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="mt-6">
  {/* Table view for medium and up */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border px-4 py-2 text-left">Month</th>
          <th className="border px-4 py-2 text-left">EMI (₹)</th>
          <th className="border px-4 py-2 text-left">Interest (₹)</th>
          <th className="border px-4 py-2 text-left">Principal (₹)</th>
          <th className="border px-4 py-2 text-left">Outstanding Principal  (₹)</th>
        </tr>
      </thead>
      <tbody>
        {[
           ["1", "44,389", "39,389", "5,000", "4,60,611"],
           ["2", "44,389", "39,782", "4,607", "4,20,829"],
           ["3", "44,389", "40,180", "4,209", "3,80,649"],
           ["4", "44,389", "40,582", "3,807", "3,40,067"],
           ["5", "44,389", "40,988", "3,401", "2,99,079"],
           ["6", "44,389", "41,398", "2,991", "2,57,681"],
           ["7", "44,389", "41,812", "2,577", "2,15,869"],
           ["8", "44,389", "42,230", "2,159", "1,73,639"],
           ["9", "44,389", "42,652", "1,737", "1,30,987"],
           ["10", "44,389", "43,079", "1,310", "87,908"],
           ["11", "44,389", "43,510", "879", "44,398"],
           ["12", "44,389", "44,398", "-", "0"]
        ].map(([month, emi, interest, principal, balance], index) => (
          <tr key={index}>
            <td className="border px-4 py-2">{month}</td>
            <td className="border px-4 py-2">{emi}</td>
            <td className="border px-4 py-2">{interest}</td>
            <td className="border px-4 py-2">{principal}</td>
            <td className="border px-4 py-2">{balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Card layout for small screens */}
  <div className="md:hidden space-y-4 mt-4">
    {[
      ["1", "44,389", "39,389", "5,000", "4,60,611"],
      ["2", "44,389", "39,782", "4,607", "4,20,829"],
      ["3", "44,389", "40,180", "4,209", "3,80,649"],
      ["4", "44,389", "40,582", "3,807", "3,40,067"],
      ["5", "44,389", "40,988", "3,401", "2,99,079"],
      ["6", "44,389", "41,398", "2,991", "2,57,681"],
      ["7", "44,389", "41,812", "2,577", "2,15,869"],
      ["8", "44,389", "42,230", "2,159", "1,73,639"],
      ["9", "44,389", "42,652", "1,737", "1,30,987"],
      ["10", "44,389", "43,079", "1,310", "87,908"],
      ["11", "44,389", "43,510", "879", "44,398"],
      ["12", "44,389", "44,398", "-", "0"]
    ].map(([month, emi, interest, principal, balance], index) => (
      <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
        <div><span className="font-semibold">Month:</span> {month}</div>
        <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
        <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
        <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
        <div><span className="font-semibold">Outstanding Principal  (₹):</span> {balance}</div>
      </div>
    ))}
  </div>
</div>

          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Comparison: Business Loan vs Personal Loan
            </h2>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Feature</th>
                    <th className="border px-4 py-2 text-left">Business Loan</th>
                    <th className="border px-4 py-2 text-left">Personal Loan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">Purpose</td>
                    <td className="border px-4 py-2">Business-related expenses</td>
                    <td className="border px-4 py-2">Personal use (wedding, travel, etc.)</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Loan Amount</td>
                    <td className="border px-4 py-2">Higher limits (₹50,000 to ₹50 Lakhs+)</td>
                    <td className="border px-4 py-2">Typically ₹50,000 to ₹25 Lakhs</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Tenure</td>
                    <td className="border px-4 py-2">Up to 5 years or more</td>
                    <td className="border px-4 py-2">Up to 5 years</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Eligibility</td>
                    <td className="border px-4 py-2">Based on business vintage, turnover</td>
                    <td className="border px-4 py-2">Based on personal income & credit score</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Interest Rate</td>
                    <td className="border px-4 py-2">Usually lower for established businesses</td>
                    <td className="border px-4 py-2">May be higher</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Collateral Required</td>
                    <td className="border px-4 py-2">Often collateral-free (unsecured loans)</td>
                    <td className="border px-4 py-2">Usually unsecured</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Tax Benefits</td>
                    <td className="border px-4 py-2">Interest may be tax-deductible</td>
                    <td className="border px-4 py-2">No tax benefit</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            {/* Desktop Table */}
<div className="hidden md:block mt-4">
<table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Feature</th>
                    <th className="border px-4 py-2 text-left">Business Loan</th>
                    <th className="border px-4 py-2 text-left">Personal Loan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">Purpose</td>
                    <td className="border px-4 py-2">Business-related expenses</td>
                    <td className="border px-4 py-2">Personal use (wedding, travel, etc.)</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Loan Amount</td>
                    <td className="border px-4 py-2">Higher limits (₹50,000 to ₹50 Lakhs+)</td>
                    <td className="border px-4 py-2">Typically ₹50,000 to ₹25 Lakhs</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Tenure</td>
                    <td className="border px-4 py-2">Up to 5 years or more</td>
                    <td className="border px-4 py-2">Up to 5 years</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Eligibility</td>
                    <td className="border px-4 py-2">Based on business vintage, turnover</td>
                    <td className="border px-4 py-2">Based on personal income & credit score</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Interest Rate</td>
                    <td className="border px-4 py-2">Usually lower for established businesses</td>
                    <td className="border px-4 py-2">May be higher</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Collateral Required</td>
                    <td className="border px-4 py-2">Often collateral-free (unsecured loans)</td>
                    <td className="border px-4 py-2">Usually unsecured</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Tax Benefits</td>
                    <td className="border px-4 py-2">Interest may be tax-deductible</td>
                    <td className="border px-4 py-2">No tax benefit</td>
                  </tr>
                </tbody>
              </table>
  
</div>

{/* Mobile Cards */}
<div className="md:hidden space-y-4 mt-4">
  {[
 {
    "feature": "Purpose",
    "business_loan": "Business-related expenses",
    "personal_loan": "Personal use (wedding, travel, etc.)"
  },
  {
    "feature": "Loan Amount",
    "business_loan": "Higher limits (₹50,000 to ₹50 Lakhs+)",
    "personal_loan": "Typically ₹50,000 to ₹25 Lakhs"
  },
  {
    "feature": "Tenure",
    "business_loan": "Up to 5 years or more",
    "personal_loan": "Up to 5 years"
  },
  {
    "feature": "Eligibility",
    "business_loan": "Based on business vintage, turnover",
    "personal_loan": "Based on personal income & credit score"
  },
  {
    "feature": "Interest Rate",
    "business_loan": "Usually lower for established businesses",
    "personal_loan": "May be higher"
  },
  {
    "feature": "Collateral Required",
    "business_loan": "Often collateral-free (unsecured loans)",
    "personal_loan": "Usually unsecured"
  },
  {
    "feature": "Tax Benefits",
    "business_loan": "Interest may be tax-deductible",
    "personal_loan": "No tax benefit"
  }
  ].map((item, index) => (
    <div key={index} className="border rounded-lg p-4 shadow-sm bg-white">
      <p className="font-semibold text-gray-700 mb-2">{item.feature}</p>
      <div className="text-sm text-gray-600">
        <p><span className="font-medium">Business Loan:</span> {item.business_loan}</p>
        <p><span className="font-medium">Personal Loan :</span> {item.personal_loan}</p>
      </div>
    </div>
  ))}
</div>

            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While both loan types serve distinct purposes, understanding their differences is crucial for choosing the right product. Business loans are tailored for revenue-generating investments, whereas personal loans cater to individual needs.
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinMaster’s Business Loan EMI Calculator
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinMaster’s Business Loan EMI Calculator is not just another utility tool—it’s a strategic asset for entrepreneurs, business owners, and financial planners looking to navigate their borrowing journey with precision. Whether you’re expanding operations, upgrading equipment, or managing working capital, this calculator gives you clear financial foresight. Here’s how it benefits you:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Quick and Accurate EMI Computation:</strong> The calculator delivers instant results the moment you enter your loan amount, interest rate, and tenure. This accuracy removes guesswork and helps you understand your monthly repayment obligation with clarity, saving time and avoiding calculation errors.</li>
              <li><strong>Customizable Inputs for Tailored Planning:</strong> Business requirements vary—from short-term liquidity needs to long-term expansion plans. TheFinMaster’s tool allows you to adjust the key parameters and simulate multiple loan scenarios, helping you find the ideal EMI that suits your repayment capacity.</li>
              <li><strong>Comprehensive Loan Breakdown:</strong> The calculator offers a clear breakup of your EMI into principal and interest components, along with a full amortization schedule. This breakdown helps you track how your payments reduce the outstanding balance over time.</li>
              <li><strong>Improved Financial Discipline:</strong> By showing you the exact EMI burden, the tool allows you to structure your monthly cash flow better. Knowing your liabilities in advance helps ensure that loan servicing does not disrupt your day-to-day operations.</li>
              <li><strong>Enables Comparison Across Lenders:</strong> Different lenders offer varying interest rates and loan conditions. The calculator helps you assess EMI outcomes under each lender’s terms, empowering you to choose the most cost-effective option for your business.</li>
              <li><strong>Strategic Decision-Making for Prepayments:</strong> Planning to prepay a part of your loan? The calculator helps you forecast how lump-sum payments can reduce your EMI or loan tenure, allowing you to make informed decisions that can save substantial interest costs.</li>
              <li><strong>Business Budgeting and Forecasting:</strong> The tool fits naturally into your broader financial planning process. It helps in forecasting your loan impact over the entire term, making it easier to plan investments, expansions, or buffer funds for slow months.</li>
              <li><strong>Accessible and Free of Cost:</strong> The calculator is completely free to use, requires no login or subscription, and can be used multiple times. Whether you're a startup founder or an SME owner, you can calculate EMIs anytime, anywhere—no strings attached.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors That Affect Your Business Loan EMI
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The EMI (Equated Monthly Installment) for a business loan is shaped by several interlinked factors. Understanding these elements can help borrowers make smart decisions, negotiate better deals, and avoid financial surprises.
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> The principal loan amount is the foundation of EMI calculation. A higher loan amount translates to higher EMIs, unless it’s offset by a longer repayment period. Business owners must evaluate their funding needs precisely to avoid over-borrowing.</li>
              <li><strong>Interest Rate:</strong> One of the most significant influencers of your EMI. Business loan rates depend on various factors like the lender’s policy, your credit profile, collateral, and business history. A small variation in interest rate can significantly change your monthly payout.</li>
              <li><strong>Loan Tenure:</strong> The duration for which the loan is borrowed plays a key role. A longer tenure lowers the EMI but increases total interest outgo, whereas a shorter tenure increases the EMI but reduces the total interest paid.</li>
              <li><strong>Business Creditworthiness:</strong> Lenders assess the credit health of your business by reviewing your credit score, balance sheet, profit and loss statements, and cash flow. A strong credit profile can fetch lower interest rates and thus a lower EMI.</li>
              <li><strong>Collateral and Loan Type:</strong> Secured business loans (against assets) generally have lower interest rates compared to unsecured loans. The type of loan you apply for will therefore influence both your EMI amount and your repayment structure.</li>
              <li><strong>Interest Type: Fixed vs Floating:</strong> Fixed interest rates remain constant throughout the loan tenure, making your EMIs predictable. Floating rates, however, vary with market conditions—leading to potential fluctuations in your EMI.</li>
              <li><strong>Prepayment or Foreclosure Terms:</strong> Some business loans offer partial or full prepayment options, which can bring down your overall liability. Knowing these terms helps in planning for lump-sum repayments and calculating the revised EMI structure accordingly.</li>
              <li><strong>Processing and Additional Fees:</strong> Though not part of the EMI, processing charges, documentation fees, and annual service charges add to the overall loan cost. Ignoring these can lead to underestimating the total impact on your finances.</li>
              <li><strong>GST and Tax Treatment:</strong> While EMIs are not taxable, the interest portion may have implications depending on your business’s accounting practices. In some cases, interest paid on business loans is tax-deductible, which may reduce the overall cost of borrowing.</li>
              <li><strong>Industry Type and Market Risk:</strong> Lenders also consider the risk associated with your industry sector. Volatile sectors may attract higher interest rates, impacting your EMI. This is especially relevant for startups or seasonal businesses.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Use a Business Loan EMI Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <li>Startups looking to evaluate funding requirements</li>
                <li>Small and Medium Enterprises (SMEs) planning expansion</li>
                <li>Established businesses needing working capital</li>
                <li>Self-employed professionals managing cash flow cycles</li>
                <li>Financial advisors evaluating various loan products for clients</li>
                <li>Business owners comparing prepayment options or refinancing possibilities</li>
              </ul>
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using this calculator helps ensure that your chosen EMI fits within your monthly business budget, avoiding stress or defaults.
            </p>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>

                       {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
   </>
  );
};

export default BusinessLoanEmiCalculator;
